import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, switchMap, takeWhile } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { AsyncSubject, interval, Observable, Subscription } from 'rxjs';
import {
    AutomaticAdjustment,
    Branch,
    City,
    ElectronicDocumentResponse,
    GetPaymentInfoResponse,
    Location,
    PaymentDataRequest,
    PaymentMethod,
    Policy,
    PolicyDraft,
    PolicyHeader,
    PremiumResponse,
    SetPaymentDataRequest
} from '../models';
import { PremiumDiscount } from '@sc/portal.fe.lib.app-components/lib/models/premium-discount.model';
import { AffinityGroup } from '@sc/portal.fe.lib.app-components/lib/models/affinity-group.model';
import { MailingAddressResponse } from '../models/mailing-address-response.model';
import { State } from '@sc/portal.fe.lib.app-components';

const RETRY_DELAY = 5000;

@Injectable({
    providedIn: 'root'
})
export class PolicyService {

    public policyStatusSubscription$: Subscription;
    public headers: any;

    constructor(
        private http: HttpClient
    ) { }

    getPremiumDiscounts(subBranch = '', commercialAlternativeKey?): Observable<PremiumDiscount[]> {
        const commercialAlternativeKeyUrl = commercialAlternativeKey ? `&commercialAlternativeKey=${commercialAlternativeKey}` : '';
        const baseUrl = environment.server.api.portalpas;

        return this.http.get(`${baseUrl}/PremiumDiscounts?subBranch=${subBranch}${commercialAlternativeKeyUrl}`)
            .pipe(
                map((response: { premiumDiscounts: { key: number, name: string }[] }) => {
                    return response.premiumDiscounts.map(item => {
                        return {
                            key: String(item.key),
                            name: item.name
                        };
                    });
                }));
    }

    getPremiumDiscountsBasedOnAG(publicId: string, branch?: string, subbranch?: string): Observable<Array<any>> {

        if (!publicId) {
            return this.getPremiumDiscounts(subbranch);
        }

        const url = `${environment.server.api.portalpas}/affinitygroup/${publicId}/commercial-alternative` +
            `?branch=${branch}&subbranch=${subbranch}`;

        return this.http.get(url).pipe(
            map((items: any[]) => items)
        );
    }

    getAutomaticAdjustments() {
        return this.http.get(`${environment.server.api.dynamo}/TypeList/name/Ext_AutomaticAdjustSum`)
            .pipe(
                map((response: any) => response.data.items.splice(1))
            );
    }

    getAutomaticAdjustmentsTypeList(effectiveDate: string, expirationDate: string) {
        const baseUrl = environment.server.api.portalpas;

        return this.http.get<AutomaticAdjustment[]>(
            `${baseUrl}/AutomaticAdjustments/auto/-1?effectiveDate=${effectiveDate}&expirationDate=${expirationDate}`
        );
    }

    getAffinityGroups(policyPeriodId: string) {
        return this.http.get(`${environment.server.api.policy}/Policy/AffinityGroup?policyPeriodId=${policyPeriodId}`,
            { headers: { 'x-id': policyPeriodId } }
        )
            .pipe(map(response => response['affinityGroups']));
    }

    getAffinityGroupsV3(policyPeriodId: string): Observable<any[]> {
        const url = `${environment.server.api.portalpas}/Policy/${policyPeriodId}/AffinityGroups`;

        return this.http.get(url).pipe(map((response: { affinityGroups: any[] }) => response.affinityGroups));
    }

    updateAffinityGroup(policyPeriodId: string, affinityGroup: Partial<AffinityGroup>): Observable<any> {
        const url = `${environment.server.api.portalpas}/Policy/${policyPeriodId}/AffinityGroups`;

        return this.http.put(url, affinityGroup);
    }

    getAffinityGroupDiscounts(affinityGroupId: string, branch?: string, subbranch?: string) {
        const params = { params: { affinityGroupId, branch, subbranch } };
        return this.http.get(`${environment.server.api.policy}/Policy/v3/AffinityGroupDiscounts`, params)
            .pipe(map(response => response['data'].items));
    }

    checkStatus(policyPeriodId, observer$: AsyncSubject<any>, maxRetries = 20, currentAttempt = 1) {
        if (this.policyStatusSubscription$) {
            this.policyStatusSubscription$.unsubscribe();
        }

        this.policyStatusSubscription$ = this.policyStatus$(policyPeriodId, maxRetries).subscribe(
            data => {
                if (data.status === 'Bound') {
                    this.policyStatusSubscription$.unsubscribe();
                    observer$.next(data);
                    observer$.complete();
                }
            },
            error => observer$.error(error),
            () => {
                if (currentAttempt < maxRetries) {
                    this.checkStatus(policyPeriodId, observer$, maxRetries, ++currentAttempt);
                }
            }
        );
    }

    getStatus(policyPeriodId: string) {
        return this.http.get(`${environment.server.api.policy}/Policy/v3/PolicyStatus?policyPeriodId=${policyPeriodId}`);
    }

    deletePolicyJob(policyPeriodId): Observable<any> {
        const url = `${environment.server.api.policy}/Policy/PolicyJob?PolicyPeriodId=${policyPeriodId}`;
        return this.http.delete(url);
    }

    private policyStatus$(policyPeriodId: string, maxRetries): Observable<any> {

        let currentRetry = 0;

        return interval(RETRY_DELAY).pipe(
            switchMap(() => {
                currentRetry++;
                return this.getStatus(policyPeriodId);
            }),
            takeWhile(() => currentRetry < maxRetries)
        );
    }

    getBranchesList(): Observable<Branch[]> {
        return this.http.get<Branch[]>(`${environment.server.api.policy}/PolicyRenewals/BranchesList`);
    }

    setCommercialAlternative(request: { policyPeriodId: string, policy: Policy }): Observable<any> {
        const url = `${environment.server.api.portalpas}/Policy/${request.policyPeriodId}/commercial-alternative`;
        const body = { PercentageValue: parseInt(request.policy.premiumDiscountKey, 10) };
        return this.http.put(url, body);
    }

    detachTemplate(policyPeriodId: string, templateID: string) {
        const url = `${environment.server.api.policy}/Policy/DetachTemplate` +
            `?policyPeriodId=${policyPeriodId}&templateID=${templateID}`;
        return this.http.delete(url);
    }

    setToDraft(policyPeriodId: string) {
        const url = `${environment.server.api.policy}/Policy/Draft?policyPeriodID=${policyPeriodId}`;
        return this.http.put(url, null);
    }

    setToDraftV3(policyPeriodId: string) {
        const url = `${environment.server.api.policy}/Submission/Draft?policyPeriodID=${policyPeriodId}`;
        return this.http.put(url, null);
    }

    createDraft(accountNumber: string, cityKey: string, subBranch: string, planId: number) {
        const url = environment.server.api.policy +
            `/policy/CombinedDraft?accountNumber=${accountNumber}` +
            `&planID=${planId}` +
            `&cityKey=${cityKey}` +
            '&channel=PortalTD' +
            `&subBranch=${subBranch}`;

        return this.http.post(url, null);
    }

    createDraftProxy(accountNumber: string, productCode: string, channelEntryCode: string): Observable<PolicyDraft> {
        const baseUrl = environment.server.api.portalpasProxy;
        const body = {
            accountNumber,
            productCode,
            channelEntryCode
        };

        return this.http.post<PolicyDraft>(`${baseUrl}/policycenter-policy/api/policies`, body);
    }

    createDraftWithSubBranch(accountNumber: string, productCode: string, policyTypeCode: string): Observable<any> {
        const url = `${environment.server.api.policy}/Submission/DraftWithSubBranch`;
        const body = {
            accountNumber: accountNumber,
            productCode: productCode,
            channelEntryCode: 'PortalTD',
            policyTypeCode: policyTypeCode
        };

        return this.http.post(url, body);
    }

    setSubBranch(policyPeriodId: string, policyTypeCode: string): Observable<void> {
        const baseUrl = environment.server.api.portalpasProxy;
        const body = { policyTypeCode };

        return this.http.patch<void>(`${baseUrl}/policycenter-policy/api/policies/${policyPeriodId}/policy-type`, body);
    }

    sendContactData(accountNumber: string, phoneNumber: string, email: string): Observable<any> {
        const url = `${environment.server.api.policy}/Policy/Contact?accountNumber=${accountNumber}`;
        const body = {
            phoneNumber: phoneNumber,
            email: email
        };

        return this.http.post(url, body);
    }

    updateRiskAddress(policyPeriodId: string, location: Location): Observable<any> {
        const baseUrl = environment.server.api.policy;
        const city: City = location && location.city;
        const address: any = location && location.address;
        const body = {
            policyPeriodId: policyPeriodId,
            cityKey: city && city.key || '',
            cityName: city && city.name || '',
            postalCode: city && city.postalCode || '',
            streetName: address && address.streetName || '-',
            streetNumber: address && address.number || '',
            displayText: '',
            floor: address && address.floor || '',
            department: address && address.department || ''
        };

        return this.http.post(`${baseUrl}/Risk/Location`, body);
    }

    setLocation(policyPeriodId: string, locationData: {
        accountNumber: string,
        postalCode: string,
        city: string,
        stateCode: string
    }): Observable<void> {
        const baseUrl = environment.server.api.portalpas;

        return this.http.post<void>(`${baseUrl}/policy/${policyPeriodId}/risk-location-by-postal-code`, locationData);
    }

    setTemplate(policyPeriodId: string, templateId: string): Observable<void> {
        const baseUrl = environment.server.api.portalpasProxy;

        return this.http.post<void>(`${baseUrl}/policycenter-policy/api/Templates/${templateId}/apply`, { policyPeriodId });
    }

    templateToAllCoberables(policyPeriodId: string, templateId: string): Observable<any> {
        const baseUrl = environment.server.api.policy;

        return this.http.post(
            `${baseUrl}/policy/TemplateToAllCoverables?policyPeriodID=${policyPeriodId}&templateID=${templateId}`, null
        );
    }

    // setPaymentDetailV3 deberia eliminarse y usar siempre este metodo
    setPaymentData(policyPeriodId: string, paymentData: PaymentMethod): Observable<any> {
        const url = `${environment.server.api.portalpas}/Policy/${policyPeriodId}/payment-data`;

        const body: PaymentDataRequest = {
            paymentMethodKey: paymentData.paymentTypeId,
            creditCardTypeKey: paymentData.creditCardType,
            creditCardNumber: paymentData.creditCardNumber,
            cbu: paymentData.cbuNumber,
            collectorKey: paymentData.collector.code,
            sendsCuponsByKey: paymentData.sendCouponsBy.code,
            paymentInstrumentId: paymentData.paymentInstrumentId,
            isExistingPayment: !!(paymentData.paymentInstrumentId)
        };

        return this.http.post(url, body);
    }

    setPaymentDetailV3(policyPeriodId: string, paymentData: PaymentMethod): Observable<any> {
        const url = `${environment.server.api.policy}/Policy/v3/PaymentDetail`;
        const request = {
            policyPeriodID: policyPeriodId,
            cbu: paymentData.cbuNumber,
            collectorKey: paymentData.collector.code,
            creditCardNumber: paymentData.creditCardNumber,
            creditCardTypeKey: paymentData.creditCardType,
            paymentMethodKey: paymentData.paymentMethodKey,
            sendsCuponsByKey: paymentData.sendCouponsBy.code,
        };

        return this.http.put(url, request);
    }

    setPaymentDataInCore(policyPeriodId: string, paymentTypeId: string, quoteNro: number): Observable<any> {
        const url = `${environment.server.api.policy}/Policy/PolicyPaymentData?policyPeriodId=${policyPeriodId}`;
        const body = {
            paymentMethodKey: paymentTypeId,
            paymentFee: quoteNro,
        };

        return this.http.post(url, body);
    }

    setPaymentDataInCoreV3(policyPeriodId: string, paymentTypeId: string, quoteNro: number): Observable<any> {
        const url = `${environment.server.api.policy}/Policy/v3/PolicyPaymentData?policyPeriodID=${policyPeriodId}`;
        const body = {
            paymentMethodKey: paymentTypeId,
            paymentFee: quoteNro,
        };

        return this.http.post(url, body);
    }

    mailingAddress(policyPeriodId: string, cityData: City, address: any = null) {
        return this.http.put(
            `${environment.server.api.policy}/Policy/MailingAdress`,
            this.getMailingAddressRequest(policyPeriodId, cityData, address));
    }

    private getMailingAddressRequest(policyPeriodId: string, city: City, address: any) {
        const state: State = city && city.state;

        return {
            policyPeriodId: policyPeriodId,
            postalCode: city && city.postalCode,
            streetName: address && address.street || '-',
            streetNumber: address && address.streetNumber || '-',
            floor: address && address.floor,
            department: address && address.department,
            cityName: city && city.name,
            state: {
                code: state && state.key,
                description: state && state.name
            }
        };
    }

    createNewPolicyVersion(policyPeriodId: string): Observable<any> {
        return this.http.post(`${environment.server.api.policy}/PolicyRenewals/NewVersionsPeriod`, { policyPeriodId }, this.headers);
    }

    selectedVersion(policyPeriodId: string): Observable<any> {
        return this.http.put(`${environment.server.api.policy}/PolicyRenewals/SelectedVersion`, { policyPeriodId }, this.headers);
    }

    electronicDocumentsMark(accountNumber: string, mark = true) {
        return this.http.put(`${environment.server.api.portalpas}/Account/ElectronicDocumentsMark/${accountNumber}`, {
            electronicDocumentsMark: mark
        });
    }

    getElectronicMarkByPolicyPeriodId(policyPeriodId: string): Observable<ElectronicDocumentResponse> {
        const baseUrl = environment.server.api.portalpas;

        return this.http.get<ElectronicDocumentResponse>(`${baseUrl}/Account/ElectronicDocumentsMarkByPolicyPeriodId?policyPeriodId=${policyPeriodId}`);
    }

    setElectronicMarkByPolicyPeriodId(policyPeriodId: string, electronicDocumentsMark: boolean) {
        const baseUrl = environment.server.api.portalpas;

        return this.http.put(`${baseUrl}/Account/ElectronicDocumentsMarkByPolicyPeriodId?policyPeriodId=${policyPeriodId}`, {
            electronicDocumentsMark
        });
    }

    primaryContactData(contactData: { phoneNumber?: string, email: string }, accountNumber, policyPeriodId): Observable<any> {
        const baseUrl = `${environment.server.api.policy}/Contact/PrimaryContactDataAp`;

        return this.http.post(
            `${baseUrl}?accountNumber=${accountNumber}&policyPeriodId=${policyPeriodId}`,
            contactData
        );
    }

    getPolicyStatus(policyPeriodId: string): Observable<any> {
        return this.http.get(
            `${environment.server.api.portalpasProxy}/policycenter-policy/api/Policies/${policyPeriodId}/status`
        );
    }

    getPremium(policyPeriodId: string): Observable<PremiumResponse> {
        return this.http.get<PremiumResponse>(`${environment.server.api.portalpasProxy}/policycenter-policy/api/Policies/${policyPeriodId}/premium`);
    }

    setPaymentInfo(body: SetPaymentDataRequest, policyPeriodId: string): Observable<any> {
        return this.http.put(`${environment.server.api.portalpas}/Policy/${policyPeriodId}/payment-info`, body);
    }

    quote(policyPeriodId: string): Observable<any> {
        const url = `${environment.server.api.portalpasProxy}/policycenter-submission/api/Quotations/${policyPeriodId}/quote`;

        return this.http.post(url, {});
    }

    getPaymentInfo(policyNumber: string, policyPeriodId: string, withoutCache: boolean = false): Observable<GetPaymentInfoResponse> {
        const endpoint = `${environment.server.api.portalpas}/Policy/payment-info/${policyPeriodId}?policyNumber=${policyNumber}&withoutCache=${withoutCache}`;

        return this.http.get<GetPaymentInfoResponse>(endpoint);
    }

    getSimplePolicyHeader(policyNumber: string): Observable<PolicyHeader> {
        return this.http.get<PolicyHeader>(`${environment.server.api.portalpas}/policy/header?policyNumber=${policyNumber}`);
    }

    getMailingAddress(policyNumber: string): Observable<MailingAddressResponse> {
        const baseUrl = environment.server.api.portalpas;
        return this.http.get<MailingAddressResponse>(`${baseUrl}/Policy/${policyNumber}/mailing-address`);
    }

    getPolicyNumberByJobNumber(jobNumber: string) {
        return jobNumber.substring(0, jobNumber.lastIndexOf('-'));
    }

    getEndorsementByJobNumber(jobNumber: string) {
        return jobNumber.substring(jobNumber.lastIndexOf('-') + 1, jobNumber.length);
    }

    deleteDraft(policyPeriodId: string) {
        const baseUrl = environment.server.api.portalpasProxy;
        return this.http.delete(`${baseUrl}/policycenter-policy/api/Policies/${policyPeriodId}`);
    }

    sendApprovalRequestProxy(policyPeriodId: string): Observable<any> {
        const url = environment.server.api.portalpasProxy +
                    `/policycenter-policy/api/Policies/${policyPeriodId}/underwriting-issues/approval-request`;
        return this.http.put(url, null);
    }
}
